import type { ExplorerFinding } from '../../../../models/explorer/ExplorerFinding';
import type { FindingRule, FindingRuleContext } from '../types';
import { groupByTier } from '../../../../canvas/tierGrouping';

/**
 * ghost_vpc / dead_vpc — VPCs with only network plumbing (or nothing
 * at all). Migrated from the legacy monolithic findings function.
 */
export const ghostVpcRule: FindingRule = {
  id: 'ghost_vpc',
  label: 'Ghost / Dead VPC',
  description: 'VPCs with no compute, no data, and no ingress.',

  run(ctx: FindingRuleContext): readonly ExplorerFinding[] {
    const findings: ExplorerFinding[] = [];
    for (const b of ctx.bubbles) {
      if (b.kind !== 'vpc') continue;
      const bands = groupByTier(b.interior ?? []);
      const count = (tier: string) =>
        bands.find((band) => band.tier === tier)?.dots.length ?? 0;
      const ingress = count('ingress');
      const compute = count('compute');
      const data = count('data');
      const shared = count('shared');
      const network = count('network');
      const hasAnyPayload = ingress > 0 || compute > 0 || data > 0;

      if (!hasAnyPayload && network > 0 && shared === 0) {
        findings.push({
          id: `ghost_vpc:bubble:${b.id}`,
          ruleId: 'ghost_vpc',
          severity: 'warn',
          scope: 'bubble',
          subject: { kind: 'bubble', id: b.id },
          title: 'Ghost VPC',
          detail: `${network} security groups / ACLs but no compute, data, or ingress — likely orphaned.`,
        });
      } else if (!hasAnyPayload && network === 0 && shared === 0) {
        findings.push({
          id: `dead_vpc:bubble:${b.id}`,
          ruleId: 'dead_vpc',
          severity: 'critical',
          scope: 'bubble',
          subject: { kind: 'bubble', id: b.id },
          title: 'Dead VPC',
          detail: 'Contains nothing. Safe to delete pending owner confirmation.',
        });
      }
    }
    return findings;
  },
};
